import type {
    BreakpointRange,
    ComponentExportStructure,
    CoreExportFormat,
    Dependencies
} from '@wix/document-services-types'
import _ from 'lodash'
import {BREAKPOINT_RANGE_TYPE, DEFAULT_BREAKPOINT_MAX} from './constants'
import type {BreakpointContext} from './types'

export const getDefaultBreakpoint = (breakpoints: Partial<BreakpointRange>[]) => {
    return _.find(breakpoints, {max: DEFAULT_BREAKPOINT_MAX})
}

export const getBreakpointsInContext = (
    breakpointVariants?: BreakpointRange[],
    dependencies?: Dependencies,
    bpContext?: Partial<BreakpointRange>[]
): BreakpointContext => {
    if (breakpointVariants) {
        return breakpointVariants
    }

    if (bpContext) {
        return bpContext
    }

    if (!dependencies?.references?.variants) {
        return undefined
    }

    return _(dependencies.references.variants)
        .values()
        .filter(variant => variant?.type === BREAKPOINT_RANGE_TYPE)
        .map(variant => {
            return {
                ...(variant.info ?? {}),
                id: variant.target,
                type: BREAKPOINT_RANGE_TYPE
            } as Partial<BreakpointRange>
        })
        .value()
}

export const forEachComponentDFSWithVariantsContext = (
    {rootComponent, components, dependencies}: CoreExportFormat,
    breakpointVariantsGetter: (component: ComponentExportStructure) => BreakpointRange[] | undefined,
    callback: (component: any, variantsInContext: any) => void
) => {
    const buildStack = (componentIds: string[], bpContext?: BreakpointContext) =>
        _.map(componentIds ?? [], (componentId: string) => ({
            component: components[componentId],
            bpContext: getBreakpointsInContext(
                breakpointVariantsGetter(components[componentId]),
                dependencies,
                bpContext
            )
        }))
    const visited = new Set<string>()
    const stack: {component: ComponentExportStructure; bpContext: BreakpointContext}[] = buildStack([rootComponent])

    while (stack.length) {
        // we always have something to pop, because of while
        const {component, bpContext} = stack.pop()!
        if (!visited.has(component.id)) {
            visited.add(component.id)
            const currentVariantsInContext = getBreakpointsInContext(
                breakpointVariantsGetter(component),
                dependencies,
                bpContext
            )
            callback(component, currentVariantsInContext)
            if (component.components) {
                stack.push(...buildStack(component.components ?? [], currentVariantsInContext))
            }
        }
    }
}
